import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MultiSelect from "@/components/MultiSelect";
import { Switch } from "@/components/ui/switch";

let cache = null;
export const invalidateLeadFields = () => { cache = null; };

export function useLeadFields() {
  const [defs, setDefs] = useState(cache || []);
  useEffect(() => {
    if (cache) return;
    api.get("/lead-fields").then(({ data }) => { cache = data; setDefs(data); }).catch(() => {});
  }, []);
  return defs;
}

export const sysField = (defs, key) => defs.find((d) => d.system && d.key === key);
export const showSys = (defs, key) => { const d = sysField(defs, key); return !d || d.visible; };
export const sysLabel = (defs, key, fallback) => sysField(defs, key)?.label || fallback;

export function isShown(d, values) {
  return (d.conditions || []).every((c) => {
    const v = values[c.field];
    if (c.op === "not_empty") return v != null && v !== "" && !(Array.isArray(v) && !v.length);
    if (c.op === "in") { const want = (Array.isArray(c.value) ? c.value : [c.value]).map(String); return (Array.isArray(v) ? v : [v]).some((x) => want.includes(String(x))); }
    return String(v ?? "") === String(c.value ?? "");
  });
}

const opts = (d) => (d.options || []).filter((o) => o.active !== false).map((o) => o.value);

export function formatValue(d, v) {
  if (v == null || v === "" || (Array.isArray(v) && !v.length)) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (d.type === "boolean") return v ? "Yes" : "No";
  if (d.type === "currency") return `₹${Number(v).toLocaleString("en-IN")}`;
  if (d.type === "percent") return `${v}%`;
  if (d.type === "datetime") return new Date(v).toLocaleString("en-IN");
  return String(v);
}

export const filterType = (t) => (["number", "currency", "percent", "formula"].includes(t) ? "number" : ["date", "datetime"].includes(t) ? "date" : ["select", "radio", "boolean"].includes(t) ? "select" : "text");

function FieldInput({ d, value, onChange, lookups }) {
  const tid = `cf-${d.key}`;
  const common = { placeholder: d.placeholder || "", "data-testid": tid };
  switch (d.type) {
    case "textarea": return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />;
    case "number": case "currency": case "percent": return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...common} />;
    case "date": return <Input type="date" value={(value || "").slice(0, 10)} onChange={(e) => onChange(e.target.value)} {...common} />;
    case "datetime": return <Input type="datetime-local" value={(value || "").slice(0, 16)} onChange={(e) => onChange(e.target.value)} {...common} />;
    case "email": return <Input type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />;
    case "url": case "file": return <Input type="url" value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} placeholder={d.placeholder || "https://…"} />;
    case "boolean": return <div className="h-10 flex items-center"><Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} /></div>;
    case "formula": return <Input value={value ?? "Calculated on save"} disabled data-testid={tid} />;
    case "select": case "radio": case "lookup": {
      const list = d.type === "lookup" ? (lookups[d.lookup_entity] || []) : opts(d).map((o) => ({ id: o, name: o }));
      return (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={tid}><SelectValue placeholder={d.placeholder || "Select"} /></SelectTrigger>
          <SelectContent className="bg-white max-h-64">{list.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    case "multiselect": case "checkbox": return <MultiSelect label={d.placeholder || "Select"} options={opts(d)} value={value || []} onChange={onChange} testid={tid} width="w-full" />;
    default: return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />;
  }
}

export function DynamicFieldInputs({ defs, values, onChange, context, form }) {
  const [lookups, setLookups] = useState({});
  const custom = defs.filter((d) => !d.system && d.visible && !d.archived && (d.forms || []).includes(form) && isShown(d, { ...context, ...values }));
  const need = [...new Set(custom.filter((d) => d.type === "lookup").map((d) => d.lookup_entity))].join(",");
  useEffect(() => {
    need.split(",").filter(Boolean).forEach((e) => {
      const url = e === "colleges" ? "/colleges" : e === "courses" ? "/finder/courses?limit=500" : "/users";
      api.get(url).then(({ data }) => setLookups((l) => ({ ...l, [e]: (data.items || data).map((x) => ({ id: x.id, name: x.name })) }))).catch(() => {});
    });
  }, [need]);
  if (!custom.length) return null;
  const sections = [...new Set(custom.map((d) => d.section || "Additional Details"))];
  return (
    <div className="space-y-4 col-span-2" data-testid="dynamic-fields">
      {sections.map((s) => (
        <div key={s}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5] mb-2 border-b border-indigo-100 pb-1">{s}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {custom.filter((d) => (d.section || "Additional Details") === s).map((d) => (
              <div key={d.key} className={d.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label>{d.label}{d.required && <span className="text-rose-500"> *</span>}</Label>
                <FieldInput d={d} value={values[d.key]} onChange={(v) => onChange({ ...values, [d.key]: v })} lookups={lookups} />
                {d.description && <div className="text-[11px] text-slate-400 mt-0.5">{d.description}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
